/* eslint-disable @typescript-eslint/no-explicit-any */
import { LocalDateTime } from 'js-joda';

import { AdvancedGridElementType } from 'types/tableBasedTemplates';
import { adjustRangeForBusinessDay, fastParseTime } from 'utils/timeUtils';
import {
  assign,
  concat,
  every,
  filter,
  find,
  first,
  forEach,
  fromPairs,
  includes,
  isEmpty,
  map,
  reject,
  remove,
  sortBy,
  uniq,
} from 'es-toolkit/compat';
import { cloneDeep, identity } from 'es-toolkit';
import { core, safeToNumber, toJSON, toJSONArray } from '@tock/coretypes';
import { dashboard } from 'types';
import { filterNilValues } from '@tock/shared/utils/typeUtils';
import { getDefaultPrice } from 'utils/experienceUtils';
import { getInheritedExperienceInfoOnTableCombos } from 'selectors/routes/table-based-templates/seatingTemplateTableCombinationSelector';
import { getTicketTypeIdFromSeatingTemplateTable } from 'state/hooks/inventoryScheduleUtils';
import { isDiningVariety, isTicketTypeEventVariety } from '@tock/shared/utils/offerings';
import AppConstants from 'constants/AppConstants';
import DayTimeRange from 'utils/DayTimeRange';
import RangeSet, { range } from 'utils/range/RangeSet';
import TableTypeConstants from 'constants/TableTypeConstants';

// There was quite a bit of code that expects the date 2018-01-01 to be passed in.
// Ideally, these functions shouldn't take in a date at all since the date itself
// doesn't matter. Cleaning this up will be a rabbit hole, so for now we're going
// to turn this arbitrary date into a constant so all the places that consume this
// date are referencing the same value.
export const TEMPLATE_INFO_BUSINESS_DAY = '2018-01-01';

export interface IWarnings {
  boughtTickets: number;
  heldTickets: number;
  nonPristineTickets: number;
}

interface SeatingTemplateInfoSpec extends Partial<dashboard.SeatingTemplateInfo> {
  // Fields that need to be defined to specify a seating template
  maxSize: number;
  startTime: string;
  ticketTypeId: number;
}

export interface SeatingTemplateInfo extends SeatingTemplateInfoSpec {
  // Fields that need to be defined on an already-created SeatingTemplateInfo
  id: number;

  // Additional fields used only in the client
  _deleted?: boolean;
}

interface SeatingTemplateTableSpec extends Partial<dashboard.SeatingTemplateTable> {
  ticketTypeId: number[];
  ticketTypeConfig: dashboard.ISeatingTemplateTableTicketTypeConfig[];
  tableId: number;
}

export interface SeatingTemplateTable extends SeatingTemplateTableSpec {
  // Fields that need to be defined on an already-created SeatingTemplateTable
  id: number;
  offeredOnline: boolean;

  // Additional fields used only in the client
  _deleted?: boolean;
}

export interface DragSelectionItem {
  dateTime: LocalDateTime;
  elementId: number;
  elementType: AdvancedGridElementType;
}

export interface State {
  allActiveTickets: any[];
  checkRequestInProgress: boolean;
  dragSelectStartItem: DragSelectionItem | null;
  currentDragSelectionItem: DragSelectionItem | null;
  dragSelectInProgress: boolean;
  selectedDynamicOptimizationTimeBeforeServiceMinutes: number | null;
  errorAfterSaving?: string;
  failedToLoad: boolean;
  generatedTemplateInfoByTime: { [id: string]: any[] };
  nextId: number;
  options: { [id: string]: any };
  previousTemplateInfo?: dashboard.SeatingTemplateInfo;
  requestInProgress: boolean;
  services: any[];
  template: dashboard.SeatingTemplate;
  templateInfoByTime: { [id: string]: any[] };
  templateInfos: SeatingTemplateInfo[];
  templateInfosInitial: SeatingTemplateInfo[];
  templateTables: SeatingTemplateTable[];
  templateTableCombinations: dashboard.SeatingTemplateTableCombination[];
  templateTableCombinationsInitial: dashboard.SeatingTemplateTableCombination[];
  templateTablesInitial: SeatingTemplateTable[];
  templateInitial: dashboard.SeatingTemplate;
  tickets: any[];
  uiState: {
    availableTickets: any[];
    ticketPrices: { [id: string]: any };
    showPrice: boolean;
    tableTypeId: number;
    maxSize: number;
    minPurchaseSize: number;
    maxPurchaseSize: number;
    variety: 'COMMUNAL_TABLE' | 'TABLE';
    allowsStragglers: boolean;
    isEvensOnly: boolean;
    ticketTypeId: number | null;
    ticketTypeDefaultPrice: number;
    templateInfoByTime: { [id: string]: any[] };
  };
  visibleTimeRange: any[];
  warnings?: IWarnings;
}

export const initialState: State = {
  allActiveTickets: [],
  checkRequestInProgress: false,
  dragSelectStartItem: null,
  currentDragSelectionItem: null,
  dragSelectInProgress: false,
  selectedDynamicOptimizationTimeBeforeServiceMinutes: null,
  errorAfterSaving: undefined,
  failedToLoad: false,
  generatedTemplateInfoByTime: {},
  nextId: -1,
  options: {},
  previousTemplateInfo: undefined,
  requestInProgress: false,
  services: [],
  template: {} as any,
  templateInfoByTime: {},
  templateInfos: [],
  templateInfosInitial: [],
  templateTables: [],
  templateTableCombinations: [],
  templateTableCombinationsInitial: [],
  templateTablesInitial: [],
  templateInitial: {} as any,
  tickets: [],
  uiState: {
    availableTickets: [],
    ticketPrices: {},
    showPrice: false,
    tableTypeId: -1,
    maxSize: 2,
    minPurchaseSize: 2,
    maxPurchaseSize: 2,
    variety: TableTypeConstants.TABLE,
    allowsStragglers: false,
    isEvensOnly: false,
    ticketTypeId: null,
    ticketTypeDefaultPrice: 0,
    templateInfoByTime: {},
  },
  visibleTimeRange: [],
  warnings: undefined,
};

function sortTemplateInfos(templateInfos: any[]): any[] {
  return sortBy(templateInfos, [
    'startTime',
    'ticketTypeId',
    'maxSize',
    'minPurchaseSize',
    'maxPurchaseSize',
    'variety',
    'allowsStragglers',
    'isEvensOnly',
    'id',
  ]);
}

function updateTemplateInfo(state: State, payload: any): State {
  const { template } = state;

  const templateInfos = cloneDeep(state.templateInfos);
  const templateInfoToUpdateOrNothing = find(templateInfos, {
    id: payload.id,
  });

  if (!templateInfoToUpdateOrNothing) {
    return state;
  }
  const templateInfoToUpdate: dashboard.SeatingTemplateInfo = assign(
    templateInfoToUpdateOrNothing,
    payload
  );

  // If a price is being updated. then change everything at the same time.
  if (payload.price !== undefined) {
    const entries = filter(
      templateInfos,
      (templateInfo) => templateInfo.startTime === templateInfoToUpdate.startTime
    );
    if (templateInfoToUpdate.variety === TableTypeConstants.COMMUNAL_TABLE) {
      forEach(entries, (templateInfoAtTheTime) => {
        if (templateInfoAtTheTime.ticketTypeId !== templateInfoToUpdate.ticketTypeId) {
          return;
        }
        templateInfoAtTheTime.price = payload.price;
      });
    } else {
      forEach(entries, (templateInfoAtTheTime) => {
        if (
          templateInfoAtTheTime.ticketTypeId !== templateInfoToUpdate.ticketTypeId ||
          templateInfoAtTheTime.maxSize !== templateInfoToUpdate.maxSize ||
          templateInfoAtTheTime.minPurchaseSize !== templateInfoToUpdate.minPurchaseSize ||
          templateInfoAtTheTime.maxPurchaseSize !== templateInfoToUpdate.maxPurchaseSize ||
          templateInfoAtTheTime.variety !== templateInfoToUpdate.variety ||
          templateInfoAtTheTime.allowsStragglers !== templateInfoToUpdate.allowsStragglers ||
          templateInfoAtTheTime.isEvensOnly !== templateInfoToUpdate.isEvensOnly
        ) {
          return;
        }
        templateInfoAtTheTime.price = payload.price;
      });
    }
  }

  if (payload.ticketTypeId !== undefined || payload.startTime !== undefined) {
    templateInfoToUpdate.price = getPrice(
      state.templateInfoByTime,
      state.tickets,
      templateInfoToUpdate.ticketTypeId,
      templateInfoToUpdate.startTime
    );
  }

  return {
    ...state,
    previousTemplateInfo: templateInfoToUpdate,
    templateInfos,
    templateInfoByTime: getTemplateInfoByTime(template, templateInfos),
  };
}

function batchUpdateTemplateInfo(state: State, payload: any) {
  const { templateInfoIds, update } = payload;
  let newState = state;
  forEach(templateInfoIds, (id) => {
    newState = updateTemplateInfo(newState, { id, ...update });
  });

  return newState;
}

function getServiceTimeRangeForType(
  template: dashboard.SeatingTemplate,
  services: core.Service[]
): DayTimeRange {
  if (isScheduledTemplate(template)) {
    return getServiceTimeRangeForScheduledTemplate(template);
  } else {
    return getServiceTimeRangeForServiceType(template.serviceType, services);
  }
}

function getServiceTimeRangeForScheduledTemplate(
  template: dashboard.SeatingTemplate
): DayTimeRange {
  const serviceTimeRange = DayTimeRange.create();
  serviceTimeRange.union(template.startTime, template.endTime);
  return serviceTimeRange;
}

function getServiceTimeRangeForServiceType(
  serviceType: core.Service.Type,
  services: core.Service[]
): DayTimeRange {
  const serviceTimeRange = DayTimeRange.create();
  filter(services, { serviceType }).forEach((service) => {
    serviceTimeRange.union(service.startTime, service.endTime);
  });
  return serviceTimeRange;
}

function getInitializedTimeMap(template: dashboard.SeatingTemplate, services: core.Service[]) {
  const dateTimes: any = {};
  forEach(getServiceTimeRangeForType(template, services).getIncrements(), (timeString) => {
    dateTimes[timeString] = [];
  });
  return dateTimes;
}

function getAllTimeMap() {
  const dateTimes: any = {};
  const range = DayTimeRange.create();
  range.union('04:00', '03:45');
  forEach(range.getIncrements(), (timeString) => {
    dateTimes[timeString] = [];
  });
  return dateTimes;
}

export function rangeSetFromRestrictions<T>(
  restrictions: T[] | undefined,
  getStartTime: (t: T) => string | null | undefined,
  getEndTime: (t: T) => string | null | undefined
) {
  let restrictionsAsRange = RangeSet.empty<LocalDateTime>();

  if (restrictions) {
    restrictions.forEach((restriction) => {
      const startTime = getStartTime(restriction);
      const endTime = getEndTime(restriction);

      if (startTime && endTime) {
        const { start, end } = adjustRangeForBusinessDay(
          TEMPLATE_INFO_BUSINESS_DAY,
          startTime,
          endTime
        );
        restrictionsAsRange = restrictionsAsRange.addRange(range(start, end));
      }
    });
  }
  return restrictionsAsRange;
}

export function isScheduledTemplate(template: dashboard.SeatingTemplate): boolean {
  return !!(template.startTime && template.endTime);
}

// @VisibleForTesting
export function getTemplateInfoByTime(
  template: any,
  templateInfosInput: any[]
): { [id: string]: any[] } {
  const templateInfos = sortTemplateInfos(templateInfosInput);
  const map = getAllTimeMap();
  templateInfos.forEach((templateInfo) => {
    if (templateInfo._deleted) {
      return;
    }
    if (!map[templateInfo.startTime]) {
      map[templateInfo.startTime] = [];
    }
    map[templateInfo.startTime].push(templateInfo);
  });
  return map;
}

function getPrice(
  templateInfoByTime: { [id: string]: any[] },
  tickets: core.TicketType[],
  ticketTypeId: number,
  timeString: string
) {
  const entries = templateInfoByTime[timeString];
  const ticketType = find(tickets, (t) => t.id === ticketTypeId);
  const entry = find(entries, (e) => e.ticketTypeId === ticketTypeId);

  return getPriceInternal(ticketType, entry);
}

const getPriceInternal = (ticketType: core.ITicketType | undefined, entry: { price: number }) => {
  if (entry) {
    return entry.price;
  }

  if (!ticketType) {
    return 0;
  }

  return getDefaultPrice(ticketType);
};

function canUseTicket(ticketType: any) {
  return ticketType.isArchived !== true && ticketType.isBookable === true;
}

export default function reducer(state: State = initialState, action: BaseTockAction): State {
  const { type, payload } = action;
  switch (type) {
    case AppConstants.GENERATE_TEMPLATE_DONE: {
      let nextId = -1;
      let templateInfos = filter(cloneDeep(state.templateInfos), (templateInfo) => {
        return templateInfo.id > 0;
      });
      templateInfos.forEach((templateInfo) => {
        templateInfo._deleted = true;
      });

      templateInfos = templateInfos.concat(
        map(toJSONArray<dashboard.SeatingTemplateInfo>(payload.templateInfo), (entry) => ({
          seatingTemplateId: state.template.id,
          ticketTypeId: entry.ticketTypeId,
          price: entry.price,
          maxSize: entry.maxSize,
          maxPurchaseSize: entry.maxPurchaseSize,
          minPurchaseSize: entry.minPurchaseSize,
          allowsStragglers: entry.allowsStragglers,
          variety: entry.variety,
          startTime: entry.startTime,
          tableState: 'AVAILABLE' as dashboard.SeatingTemplateInfo.TableState,
          id: nextId--,
        }))
      );
      return {
        ...state,
        nextId,
        templateInfos,
        visibleTimeRange: getInitializedTimeMap(state.template, state.services),
        templateInfoByTime: getTemplateInfoByTime(state.template, templateInfos),
        requestInProgress: false,
      };
    }
    case AppConstants.TEMPLATE_INFO_FETCH_ALL_DONE: {
      const batchResponses = payload;
      const template = toJSON<dashboard.SeatingTemplate>(batchResponses[0]);
      const templateInfos = sortTemplateInfos(
        toJSONArray<dashboard.SeatingTemplateInfo>(batchResponses[1])
      );
      const templateTables = map(
        toJSONArray<dashboard.SeatingTemplateTable>(batchResponses[2]),
        (templateTable) => {
          if (!templateTable.tableId) {
            return {
              ...templateTable,
              _deleted: true,
            };
          } else {
            return templateTable;
          }
        }
      );
      const templateTableCombinations = toJSONArray<dashboard.SeatingTemplateTableCombination>(
        batchResponses[3]
      );
      const ticketTypes = batchResponses[4];
      const ticketOptions = batchResponses[5];
      const services = toJSONArray<core.Service>(batchResponses[6]);
      const existingTickets = uniq(
        concat(
          map(templateInfos, (t) => t.ticketTypeId),
          map(templateTables, (t) => getTicketTypeIdFromSeatingTemplateTable(t))
        )
      );
      return {
        ...state,
        template,
        templateInitial: cloneDeep(template),
        templateInfos,
        templateInfosInitial: cloneDeep(templateInfos),
        templateTables,
        templateTablesInitial: cloneDeep(templateTables),
        templateTableCombinations,
        templateTableCombinationsInitial: cloneDeep(templateTableCombinations),
        visibleTimeRange: getInitializedTimeMap(template, services),
        templateInfoByTime: getTemplateInfoByTime(template, templateInfos),
        allActiveTickets: sortBy(
          filter(ticketTypes.map(toJSON), (ticketType) => {
            return canUseTicket(ticketType);
          }),
          // put dining experiences ahead of to-go ones as a quick way to use the bright/ideal colors
          // this list is exclusively used to map the index of an experience to a
          // label color within a blueprint
          (ticketType) => (isDiningVariety(ticketType.variety) ? 0 : 1)
        ),
        tickets: filter(ticketTypes.map(toJSON), (ticketType: core.TicketType) => {
          return (
            (includes(existingTickets, ticketType.id) || canUseTicket(ticketType)) &&
            !isTicketTypeEventVariety(ticketType) &&
            (isScheduledTemplate(template) ||
              includes(ticketType.serviceType, template.serviceType) ||
              includes(existingTickets, ticketType.id))
          );
        }),
        options: fromPairs(
          ticketOptions.map((ticketOption: any) => {
            return [safeToNumber(ticketOption.id), toJSON(ticketOption)];
          })
        ),
        services,
        requestInProgress: false,
      };
    }
    case AppConstants.TEMPLATE_INFO_RESET_TEMPLATE_INFOS:
      return {
        ...state,
        templateInfos: payload,
      };
    case AppConstants.TEMPLATE_INFO_RESET_TABLES:
      return {
        ...state,
        template: {
          ...state.template,
          floorplan: { ...state.template.floorplan, table: payload, activeTable: payload },
        },
      };
    case AppConstants.TEMPLATE_INFO_RESET_TABLE_COMBINATIONS:
      return {
        ...state,
        template: {
          ...state.template,
          floorplan: { ...state.template.floorplan, tableCombination: payload },
        },
      };
    case AppConstants.TEMPLATE_INFO_RESET_TEMPLATE_TABLES:
      return {
        ...state,
        templateTables: payload,
      };
    case AppConstants.TEMPLATE_INFO_RESET_TEMPLATE_TABLE_COMBINATIONS:
      return {
        ...state,
        templateTableCombinations: payload,
      };
    case AppConstants.TEMPLATE_INFO_SAVE_ALL_DONE: {
      const setSeatingTemplateResponse = toJSON<dashboard.SetSeatingTemplateResponse>(payload);
      let errorAfterSaving: string | undefined;
      const template = setSeatingTemplateResponse.seatingTemplate as dashboard.SeatingTemplate;
      const templateInfos = sortTemplateInfos(
        setSeatingTemplateResponse.seatingTemplateInfo as dashboard.SeatingTemplateInfo[]
      );
      const templateTables =
        setSeatingTemplateResponse.seatingTemplateTable as dashboard.SeatingTemplateTable[];
      const templateTableCombinations =
        setSeatingTemplateResponse.seatingTemplateTableCombination as dashboard.SeatingTemplateTableCombination[];
      return {
        ...state,
        errorAfterSaving,
        template: cloneDeep(template),
        templateInitial: cloneDeep(template),
        templateInfos,
        templateInfosInitial: cloneDeep(templateInfos),
        templateTables,
        templateTableCombinations,
        templateTablesInitial: cloneDeep(templateTables),
        templateTableCombinationsInitial: cloneDeep(templateTableCombinations),
        templateInfoByTime: getTemplateInfoByTime(template, templateInfos),
        requestInProgress: false,
      };
    }
    case AppConstants.TEMPLATE_INFO_UI_STATE_PLACE_HOLDS_DONE: {
      const templateInfoIds: number[] = payload;
      const matchingTemplateInfos = filter(state.templateInfos, (templateInfo) => {
        return includes(templateInfoIds, templateInfo.id);
      });
      const areAllHeldAlready = every(matchingTemplateInfos, (templateInfo) => {
        return templateInfo.heldTickets && templateInfo.heldTickets > 0;
      });
      const templateInfos = map(state.templateInfos, (templateInfo): SeatingTemplateInfo => {
        if (!includes(templateInfoIds, templateInfo.id)) {
          return templateInfo;
        }
        return {
          ...templateInfo,
          heldTickets: areAllHeldAlready ? 0 : templateInfo.maxSize,
          tableState:
            templateInfo.variety === 'COMMUNAL_TABLE' || areAllHeldAlready ? 'AVAILABLE' : 'HELD',
        };
      });
      return {
        ...state,
        templateInfos,
      };
    }
    case AppConstants.TEMPLATE_INFO_UI_STATE_REMOVE_HOLDS_DONE: {
      const templateInfoIds: number[] = payload;
      const templateInfos = map(state.templateInfos, (templateInfo): SeatingTemplateInfo => {
        if (!includes(templateInfoIds, templateInfo.id)) {
          return templateInfo;
        }
        return {
          ...templateInfo,
          heldTickets: 0,
          tableState: 'AVAILABLE',
        };
      });
      return {
        ...state,
        templateInfos,
      };
    }
    case AppConstants.TEMPLATE_INFO_UI_STATE_UPDATE_FILTER_DONE:
      return {
        ...state,
        uiState: {
          ...state.uiState,
          ...payload,
        },
      };
    case AppConstants.TEMPLATE_INFO_UI_STATE_UPDATE_TEMPLATE_DONE: {
      const newTemplate = {
        ...state.template,
        ...payload,
      };
      let visibleTimeRange = state.visibleTimeRange;
      if (
        !isEmpty(state.template) &&
        (newTemplate.serviceType !== state.template.serviceType || isScheduledTemplate(newTemplate))
      ) {
        visibleTimeRange = getInitializedTimeMap(newTemplate, state.services);
      }
      return {
        ...state,
        visibleTimeRange,
        template: newTemplate,
      };
    }
    case AppConstants.TEMPLATE_INFO_UI_STATE_ADD_COMPLETE_TEMPLATE_INFO: {
      return updateStateWithNewTemplateInfo(payload, state);
    }
    case AppConstants.TEMPLATE_INFO_UI_STATE_ADD_TEMPLATE_INFO_DONE: {
      const { uiState } = state;

      if (uiState.ticketTypeId == null) {
        return state;
      }
      const timeString = payload;
      const newTemplateInfo = {
        allowsStragglers: !!uiState.allowsStragglers,
        isEvensOnly: !!uiState.isEvensOnly,
        maxPurchaseSize: uiState.maxPurchaseSize,
        maxSize: uiState.maxSize,
        minPurchaseSize: uiState.minPurchaseSize,
        startTime: timeString,
        ticketTypeId: uiState.ticketTypeId,
        variety: uiState.variety,
      };

      return updateStateWithNewTemplateInfo(newTemplateInfo, state);
    }
    case AppConstants.TEMPLATE_INFO_UI_STATE_UPDATE_TEMPLATE_INFO_DONE: {
      return updateTemplateInfo(state, payload);
    }
    case AppConstants.TEMPLATE_INFO_UI_STATE_BATCH_UPDATE_TEMPLATE_INFO_DONE: {
      return batchUpdateTemplateInfo(state, payload);
    }
    case AppConstants.TEMPLATE_INFO_UI_STATE_DELETE_TEMPLATE_INFO_DONE: {
      const { template } = state;
      const templateInfos = cloneDeep(state.templateInfos);
      if (payload < 0) {
        remove(templateInfos, {
          id: payload,
        });
      } else {
        templateInfos.forEach((templateInfo) => {
          if (templateInfo.id === payload) {
            templateInfo._deleted = true;
          }
        });
      }
      return {
        ...state,
        templateInfos,
        templateInfoByTime: getTemplateInfoByTime(template, templateInfos),
      };
    }
    case AppConstants.TEMPLATE_INFO_UI_STATE_BATCH_REMOVE_DONE: {
      let templateInfos: SeatingTemplateInfo[] = cloneDeep(state.templateInfos);
      forEach(templateInfos, (templateInfo) => {
        if (includes(payload, templateInfo.id)) {
          templateInfo._deleted = true;
        }
      });
      templateInfos = reject(templateInfos, (templateInfo) => {
        return templateInfo._deleted && templateInfo.id < 0;
      }) as SeatingTemplateInfo[];
      return {
        ...state,
        templateInfos,
        templateInfoByTime: getTemplateInfoByTime(state.template, templateInfos),
      };
    }
    case AppConstants.TEMPLATE_INFO_UI_STATE_TABLE_GROUP_ADD_DONE: {
      if (isEmpty(state.template)) {
        return state;
      }
      const template = state.template;
      return {
        ...state,
        nextId: state.nextId - 1,
        template: {
          ...template,
          tableGroup: template.tableGroup.concat([
            {
              id: state.nextId,
              tableId: sortBy(payload.groupedTableIds, identity),
            },
          ]),
        },
      };
    }
    case AppConstants.TEMPLATE_INFO_UI_STATE_TABLE_GROUP_REMOVE_DONE: {
      if (isEmpty(state.template)) {
        return state;
      }
      const template = state.template;
      return {
        ...state,
        template: {
          ...template,
          tableGroup: filter(template.tableGroup, (group) => group.id !== payload.tableGroupId),
        },
      };
    }
    case AppConstants.TEMPLATE_INFO_SET_TABLES_FULLY_FLEXED: {
      if (isEmpty(state.template)) {
        return state;
      }
      const { tableIds, isFullyFlexed, ticketTypeId: ticketTypeIdFromPayload } = payload;
      // Note that state.tickets is tickets usable in this template. They are active, bookable, and
      // match the template's serviceType. state.allActiveTickets doesn't necessarily match the template's serviceType
      const ticketTypeId =
        ticketTypeIdFromPayload || first(map(state.tickets, (ticketType) => ticketType.id));

      let nextId = state.nextId;
      const tablesToUpdate: { [tabledId: number]: SeatingTemplateTable[] } = {};
      const updatedTemplateTables: SeatingTemplateTable[] = [];

      // Split current template tables out in to two sets.
      // A map of tableIds => [templates tables]
      // An array of templates tables that are not assigned to the tables being modified
      forEach(state.templateTables, (table) => {
        if (tableIds.includes(table.tableId)) {
          tablesToUpdate[table.tableId] = tablesToUpdate[table.tableId]
            ? [...tablesToUpdate[table.tableId], table]
            : [table];
        } else {
          updatedTemplateTables.push(table);
        }
      });

      forEach(tableIds, (tableId) => {
        const tableToUpdate = first(tablesToUpdate[tableId]);

        if (tableToUpdate) {
          updatedTemplateTables.push({
            ...tableToUpdate,
            _deleted: !isFullyFlexed,
            ticketTypeId: [ticketTypeId],
            ticketTypeConfig: [
              isFullyFlexed
                ? getNewTicketTypeConfigForFullServiceTicketType(ticketTypeId)
                : getNewTicketTypeConfigWithEmptyBookableRanges(ticketTypeId),
            ],
          });
        } else if (isFullyFlexed) {
          updatedTemplateTables.push({
            id: nextId--,
            tableId,
            seatingTemplateId: state.template.id,
            ticketTypeId: [ticketTypeId],
            offeredOnline: true,
            ticketTypeConfig: [getNewTicketTypeConfigForFullServiceTicketType(ticketTypeId)],
          });
        }
      });

      return {
        ...state,
        nextId,
        templateTables: updatedTemplateTables,
      };
    }

    case AppConstants.TEMPLATE_INFO_SET_TABLES_BLOCKS: {
      if (isEmpty(state.template)) {
        return state;
      }
      const { mode, tableIds, start, end } = payload;

      // Build a new array of template tables.
      const templateTables: SeatingTemplateTable[] = state.templateTables.map((templateTable) => {
        if (!includes(tableIds, templateTable.tableId)) {
          return templateTable;
        }

        let blockRangeSet = rangeSetFromRestrictions(
          templateTable.block,
          ({ startTime }) => startTime,
          ({ endTime }) => endTime
        );

        switch (mode) {
          case 'ADD':
            blockRangeSet = blockRangeSet.addRange(range(start, end));
            break;

          default:
            // remove
            blockRangeSet = blockRangeSet.removeRange(range(start, end));
        }

        return {
          ...templateTable,
          block: blockRangeSet.ranges().map(({ low, high }) => ({
            startTime: low.toLocalTime().toString(),
            endTime: high.toLocalTime().toString(),
          })),
        };
      });

      return {
        ...state,
        templateTables,
      };
    }

    case AppConstants.TEMPLATE_INFO_SET_TABLES_BOOKABLE_RANGE: {
      if (isEmpty(state.template)) {
        return state;
      }

      const {
        mode,
        tableIds,
        start,
        end,
        ticketTypeId,
      }: {
        tableIds: number[];
        start: LocalDateTime;
        end: LocalDateTime;
        mode: 'ADD' | 'REMOVE';
        ticketTypeId: number;
      } = payload;

      const tableIdsSet = new Set(tableIds);

      // Build a new array of template tables.
      const templateTables: SeatingTemplateTable[] = state.templateTables.map((templateTable) => {
        if (!tableIdsSet.has(templateTable.tableId)) {
          return templateTable;
        }

        const existingTicketTypeConfigs = templateTable.ticketTypeConfig ?? [];

        const {
          bookableRangesFromRangeSet,
          existingTicketTypeConfigsForOtherTicketTypes,
          isFullServiceApplicationDesired,
          templateRange,
          unchangedRanges,
        } = manageBookableRangesForTable(
          existingTicketTypeConfigs,
          ticketTypeId,
          state.template.startTime,
          state.template.endTime,
          mode,
          start,
          end
        );

        // Handle adding / removing ticketTypeIds to the table as necessary.
        // Future cleanup: remove ticketTypeId from SeatingTemplateTable in favor of always using new ticketTypeConfig field?
        const ticketTypeIdsOnTable = new Set(templateTable.ticketTypeId);
        const hasBookableRangesOnTable = bookableRangesFromRangeSet.length > 0;
        if (hasBookableRangesOnTable) {
          ticketTypeIdsOnTable.add(ticketTypeId);
        } else {
          ticketTypeIdsOnTable.delete(ticketTypeId);
        }

        const tableRemainsActive =
          hasBookableRangesOnTable || existingTicketTypeConfigsForOtherTicketTypes.length > 0;

        return {
          ...templateTable,
          ticketTypeId: Array.from(ticketTypeIdsOnTable),
          _deleted: !tableRemainsActive,
          ticketTypeConfig: hasBookableRangesOnTable
            ? [
                ...existingTicketTypeConfigsForOtherTicketTypes,
                getUpdatedSTTTicketTypeConfig(
                  ticketTypeId,
                  isFullServiceApplicationDesired,
                  bookableRangesFromRangeSet,
                  templateRange,
                  unchangedRanges
                ),
              ]
            : existingTicketTypeConfigsForOtherTicketTypes,
        };
      });

      return {
        ...state,
        templateTables,
      };
    }

    case AppConstants.TEMPLATE_INFO_SET_TABLE_COMBOS_BOOKABLE_RANGE: {
      if (isEmpty(state.template)) {
        return state;
      }

      const {
        mode,
        tableCombinationIds,
        start,
        end,
        ticketTypeId,
      }: {
        tableCombinationIds: number[];
        start: LocalDateTime;
        end: LocalDateTime;
        mode: 'ADD' | 'REMOVE';
        ticketTypeId: number;
      } = payload;

      const tableCombinationIdsSet = new Set(tableCombinationIds);

      // Build a new array of template tables.
      const templateTableCombinations: dashboard.SeatingTemplateTableCombination[] =
        state.templateTableCombinations.map((templateTableCombination) => {
          if (!tableCombinationIdsSet.has(templateTableCombination.tableCombinationId)) {
            return templateTableCombination;
          }

          const existingTicketTypeConfigs = templateTableCombination.ticketTypeConfig ?? [];

          if (existingTicketTypeConfigs.length === 0) {
            initializeTableCombinationConfigsFromConstituentTables(
              state,
              templateTableCombination.tableCombinationId
            ).forEach((config) => existingTicketTypeConfigs.push(config));
          }

          const {
            bookableRangesFromRangeSet,
            existingTicketTypeConfigsForOtherTicketTypes,
            isFullServiceApplicationDesired,
            templateRange,
            unchangedRanges,
          } = manageBookableRangesForTable(
            existingTicketTypeConfigs,
            ticketTypeId,
            state.template.startTime,
            state.template.endTime,
            mode,
            start,
            end
          );

          const hasBookableRangesOnTable = bookableRangesFromRangeSet.length > 0;

          return {
            ...templateTableCombination,
            ticketTypeConfig: hasBookableRangesOnTable
              ? [
                  ...existingTicketTypeConfigsForOtherTicketTypes,
                  getUpdatedSTTTicketTypeConfig(
                    ticketTypeId,
                    isFullServiceApplicationDesired,
                    bookableRangesFromRangeSet,
                    templateRange,
                    unchangedRanges
                  ),
                ]
              : existingTicketTypeConfigsForOtherTicketTypes,
          };
        });

      return {
        ...state,
        templateTableCombinations,
      };
    }

    case AppConstants.TEMPLATE_INFO_SET_TABLE_RANGE_CONDITIONS: {
      if (isEmpty(state.template)) {
        return state;
      }

      const {
        mode,
        tableIds,
        effectiveAtMinutesBefore,
        ticketTypeId,
      }: {
        tableIds: number[];
        mode: 'ADD' | 'REMOVE';
        ticketTypeId: number;
        effectiveAtMinutesBefore: number | undefined;
      } = payload;

      const tableIdsSet = new Set(tableIds);

      // Build a new array of template tables.
      const templateTables: SeatingTemplateTable[] = state.templateTables.map((templateTable) => {
        if (!tableIdsSet.has(templateTable.tableId)) {
          return templateTable;
        }
        // Get the configs associated with the ticket type for which availability is being modified.
        // If it doesn't exist, create a full-service range.
        const configForSelectedTicketType: dashboard.ISeatingTemplateTableTicketTypeConfig =
          templateTable.ticketTypeConfig?.find((config) => config.ticketTypeId === ticketTypeId) ??
          getNewTicketTypeConfigForFullServiceTicketType(ticketTypeId);

        // Get the conditional range to be referenced as a "template" containing a representation of the desired bookable ranges
        // for a given experience / table (e.g., the bookable ranges set via the advanced grid).
        const { templateRange } = getConditionalRangeTemplateForSTTTicketTypeConfig(
          configForSelectedTicketType
        );

        const updatedTicketTypeIds = new Set(templateTable.ticketTypeId);
        updatedTicketTypeIds.add(ticketTypeId);

        const configsForOtherTicketTypes =
          templateTable.ticketTypeConfig?.filter(
            (config) => config.ticketTypeId !== ticketTypeId
          ) ?? [];

        const newConditionalRanges = createNewConditionalRangesWithOptimization(
          mode,
          templateRange,
          effectiveAtMinutesBefore
        );

        return {
          ...templateTable,
          ticketTypeId: Array.from(updatedTicketTypeIds),
          ticketTypeConfig: [
            ...configsForOtherTicketTypes,
            { ...configForSelectedTicketType, conditionalRanges: newConditionalRanges },
          ],
        };
      });

      return {
        ...state,
        templateTables,
      };
    }

    case AppConstants.TEMPLATE_INFO_SET_TABLE_SIZE_CONFIGS: {
      if (isEmpty(state.template)) {
        return state;
      }

      const {
        tableIds,
        sizeConfigs,
      }: {
        tableIds: number[];
        sizeConfigs: dashboard.SeatingTemplateTableSize[];
      } = payload;

      const tableIdsSet = new Set(tableIds);

      const templateTables: SeatingTemplateTable[] = state.templateTables.map((templateTable) => {
        if (!tableIdsSet.has(templateTable.tableId)) {
          return templateTable;
        }

        return {
          ...templateTable,
          tableSizeConfig: sizeConfigs,
        };
      });

      return {
        ...state,
        templateTables,
      };
    }

    case AppConstants.TEMPLATE_INFO_ADD_TEMPLATE_SIZE_CONFIG: {
      if (isEmpty(state.template)) {
        return state;
      }

      const {
        tableIds,
        minPurchaseSize,
        timeBeforeServiceMinutes,
      }: {
        tableIds: number[];
        minPurchaseSize: number;
        timeBeforeServiceMinutes: number;
      } = payload;

      const tableIdsSet = new Set(tableIds);

      const templateTables: SeatingTemplateTable[] = state.templateTables.map((templateTable) => {
        if (!tableIdsSet.has(templateTable.tableId)) {
          return templateTable;
        }

        const filteredOptimizations = templateTable.tableSizeConfig?.filter(
          (config) =>
            config.minPurchaseSize !== minPurchaseSize &&
            config.effectiveAtMinutesBefore !== timeBeforeServiceMinutes
        );

        return {
          ...templateTable,
          tableSizeConfig: [
            ...(filteredOptimizations || []),
            { minPurchaseSize, effectiveAtMinutesBefore: timeBeforeServiceMinutes },
          ],
        };
      });

      return {
        ...state,
        templateTables,
      };
    }

    case AppConstants.TEMPLATE_INFO_CLEAR_TABLE_SIZE_CONFIGS: {
      if (isEmpty(state.template)) {
        return state;
      }

      const {
        tableIds,
      }: {
        tableIds: number[];
      } = payload;

      const tableIdsSet = new Set(tableIds);

      const templateTables: SeatingTemplateTable[] = state.templateTables.map((templateTable) => {
        if (!tableIdsSet.has(templateTable.tableId)) {
          return templateTable;
        }

        return {
          ...templateTable,
          tableSizeConfig: [],
        };
      });

      return {
        ...state,
        templateTables,
      };
    }

    case AppConstants.TEMPLATE_INFO_REMOVE_TABLE_SIZE_CONFIG: {
      if (isEmpty(state.template)) {
        return state;
      }

      const {
        tableIds,
        effectiveAtMinutesBefore,
        minPurchaseSize,
      }: {
        tableIds: number[];
        effectiveAtMinutesBefore: number;
        minPurchaseSize: number;
      } = payload;

      const tableIdsSet = new Set(tableIds);

      const templateTables: SeatingTemplateTable[] = state.templateTables.map((templateTable) => {
        if (!tableIdsSet.has(templateTable.tableId)) {
          return templateTable;
        }

        const existingTableSizeConfigs = templateTable.tableSizeConfig ?? [];

        const updatedTableSizeConfigs = existingTableSizeConfigs.filter(
          (sizeConfig) =>
            !(
              sizeConfig.effectiveAtMinutesBefore === effectiveAtMinutesBefore &&
              sizeConfig.minPurchaseSize === minPurchaseSize
            )
        );

        return {
          ...templateTable,
          tableSizeConfig: updatedTableSizeConfigs,
        };
      });

      return {
        ...state,
        templateTables,
      };
    }

    case AppConstants.TEMPLATE_INFO_SET_EXPERIENCE_ON_TABLE_COMBOS: {
      const {
        tableCombinationId,
        ticketTypeId,
      }: {
        tableCombinationId: number;
        ticketTypeId: number;
      } = payload;

      return {
        ...state,
        templateTableCombinations: state.templateTableCombinations.map(
          (templateTableCombination) => {
            if (templateTableCombination.tableCombinationId === tableCombinationId) {
              const existingTicketTypeConfigs = templateTableCombination.ticketTypeConfig ?? [];

              if (existingTicketTypeConfigs.length === 0) {
                initializeTableCombinationConfigsFromConstituentTables(
                  state,
                  tableCombinationId
                ).forEach((config) => existingTicketTypeConfigs.push(config));
              }

              if (
                existingTicketTypeConfigs.find(
                  (ticketTypeConfig) => ticketTypeConfig.ticketTypeId === ticketTypeId
                )
              ) {
                return templateTableCombination;
              }

              const newTicketTypeConfig =
                getNewTicketTypeConfigForFullServiceTicketType(ticketTypeId);

              return {
                ...templateTableCombination,
                ticketTypeConfig: [...existingTicketTypeConfigs, newTicketTypeConfig],
              };
            }
            return templateTableCombination;
          }
        ),
      };
    }

    case AppConstants.TEMPLATE_INFO_REMOVE_EXPERIENCE_ON_TABLE_COMBOS: {
      const {
        tableCombinationId,
        ticketTypeId,
      }: {
        tableCombinationId: number;
        ticketTypeId: number;
      } = payload;
      return {
        ...state,
        templateTableCombinations: state.templateTableCombinations.map(
          (templateTableCombination) => {
            if (templateTableCombination.tableCombinationId === tableCombinationId) {
              const existingTicketTypeConfigs = templateTableCombination.ticketTypeConfig ?? [];
              if (existingTicketTypeConfigs.length === 0) {
                initializeTableCombinationConfigsFromConstituentTables(
                  state,
                  tableCombinationId
                ).forEach((config) => existingTicketTypeConfigs.push(config));
              }

              const filteredConfigs = existingTicketTypeConfigs.filter(
                (config) => config.ticketTypeId !== ticketTypeId
              );

              return {
                ...templateTableCombination,
                ticketTypeConfig: filteredConfigs,
              };
            }
            return templateTableCombination;
          }
        ),
      };
    }

    case AppConstants.TEMPLATE_INFO_CLEAR_EXPERIENCES_ON_TABLE_COMBO: {
      if (isEmpty(state.template)) {
        return state;
      }

      const {
        tableCombinationId,
      }: {
        tableCombinationId: number;
      } = payload;

      const templateTableCombinations: dashboard.SeatingTemplateTableCombination[] =
        state.templateTableCombinations.map((templateTableCombo) => {
          if (!(templateTableCombo.tableCombinationId === tableCombinationId)) {
            return templateTableCombo;
          }

          return {
            ...templateTableCombo,
            ticketTypeConfig: [],
          };
        });

      return {
        ...state,
        templateTableCombinations,
      };
    }

    case AppConstants.TEMPLATE_INFO_CLEAR_TABLE_RANGE_CONDITIONS: {
      if (isEmpty(state.template)) {
        return state;
      }

      const {
        tableIds,
      }: {
        tableIds: number[];
      } = payload;

      const tableIdsSet = new Set(tableIds);

      // Build a new array of template tables.
      const templateTables: SeatingTemplateTable[] = state.templateTables.map((templateTable) => {
        if (!tableIdsSet.has(templateTable.tableId)) {
          return templateTable;
        }

        const ranges: dashboard.ISeatingTemplateTableTicketTypeConfig[] =
          templateTable.ticketTypeConfig.map((config) => {
            const { templateRange } = getConditionalRangeTemplateForSTTTicketTypeConfig(config);

            return {
              ...config,
              conditionalRanges: [
                { bookableRanges: templateRange.bookableRanges, condition: undefined },
              ],
            };
          });

        return {
          ...templateTable,
          ticketTypeConfig: ranges,
        };
      });

      return {
        ...state,
        templateTables,
      };
    }

    case AppConstants.TEMPLATE_INFO_SET_TABLES_HOLDS: {
      if (isEmpty(state.template)) {
        return state;
      }
      const { mode, tableIds, start, end } = payload;

      const templateTables: SeatingTemplateTable[] = state.templateTables.map((templateTable) => {
        if (!includes(tableIds, templateTable.tableId)) {
          return templateTable;
        }

        let holdRangeSet = rangeSetFromRestrictions(
          templateTable.hold,
          ({ startTime }) => startTime,
          ({ finishedByTime }) => finishedByTime
        );

        switch (mode) {
          case 'ADD':
            holdRangeSet = holdRangeSet.addRange(range(start, end));
            break;

          default:
            // remove
            holdRangeSet = holdRangeSet.removeRange(range(start, end));
        }

        return {
          ...templateTable,
          hold: holdRangeSet.ranges().map(({ low, high }) => ({
            startTime: low.toLocalTime().toString(),
            finishedByTime: high.toLocalTime().toString(),
          })),
        };
      });

      return {
        ...state,
        templateTables,
      };
    }

    case AppConstants.TEMPLATE_INFO_SET_FULLY_FLEXED_TICKET_TYPE: {
      if (isEmpty(state.template)) {
        return state;
      }
      const { ticketTypeIds, tableIds }: { ticketTypeIds: Set<number>; tableIds: Set<number> } =
        payload;

      return replaceTicketTypesForSeatingTemplateTables(state, tableIds, () => [...ticketTypeIds]);
    }

    case AppConstants.TEMPLATE_INFO_ADD_FULLY_FLEXED_TICKET_TYPE: {
      if (isEmpty(state.template)) {
        return state;
      }
      const { ticketTypeId, tableIds }: { ticketTypeId: number; tableIds: Set<number> } = payload;

      return replaceTicketTypesForSeatingTemplateTables(
        state,
        tableIds,
        (existingTicketTypeIds) => [...existingTicketTypeIds, ticketTypeId]
      );
    }

    case AppConstants.TEMPLATE_INFO_REMOVE_FULLY_FLEXED_TICKET_TYPE: {
      if (isEmpty(state.template)) {
        return state;
      }
      const { ticketTypeId, tableIds }: { ticketTypeId: number; tableIds: Set<number> } = payload;

      return replaceTicketTypesForSeatingTemplateTables(state, tableIds, (existingTicketTypeIds) =>
        existingTicketTypeIds.filter(
          (existingTicketTypeIds) => existingTicketTypeIds !== ticketTypeId
        )
      );
    }

    case AppConstants.TEMPLATE_INFO_SET_TABLE_OFFERED_ONLINE: {
      if (isEmpty(state.template)) {
        return state;
      }
      const { tableId, offeredOnline }: { tableId: number; offeredOnline: boolean } = payload;
      return {
        ...state,
        templateTables: map(state.templateTables, (templateTable) => {
          if (templateTable.tableId === tableId) {
            return {
              ...templateTable,
              offeredOnline,
            };
          }
          return templateTable;
        }),
      };
    }

    case AppConstants.TEMPLATE_INFO_SET_ASSIGNMENT_PRIORITY_LEVEL: {
      if (isEmpty(state.template)) {
        return state;
      }
      const {
        tableId,
        priorityLevel,
      }: { tableId: number; priorityLevel: dashboard.TableAssignmentPriorityLevelProtoEnum } =
        payload;
      return {
        ...state,
        templateTables: map(state.templateTables, (templateTable) => {
          if (templateTable.tableId === tableId) {
            return {
              ...templateTable,
              assignmentPriorityLevel: priorityLevel,
            };
          }
          return templateTable;
        }),
      };
    }

    case AppConstants.TEMPLATE_INFO_SET_TABLE_COMBINATION_OFFERED_ONLINE: {
      const {
        tableCombinationId,
        offeredOnline,
      }: {
        tableCombinationId: number;
        offeredOnline: boolean;
      } = payload;
      return {
        ...state,
        templateTableCombinations: map(
          state.templateTableCombinations,
          (templateTableCombination) => {
            if (templateTableCombination.tableCombinationId === tableCombinationId) {
              return {
                ...templateTableCombination,
                offeredOnline,
              };
            }
            return templateTableCombination;
          }
        ),
      };
    }

    case AppConstants.TEMPLATE_INFO_SET_TABLE_COMBINATION_ASSIGNMENT_PRIORITY_LEVEL: {
      const {
        tableCombinationId,
        priorityLevel,
      }: {
        tableCombinationId: number;
        priorityLevel: dashboard.TableAssignmentPriorityLevelProtoEnum;
      } = payload;
      return {
        ...state,
        templateTableCombinations: map(
          state.templateTableCombinations,
          (templateTableCombination) => {
            if (templateTableCombination.tableCombinationId === tableCombinationId) {
              return {
                ...templateTableCombination,
                assignmentPriorityLevel: priorityLevel,
              };
            }
            return templateTableCombination;
          }
        ),
      };
    }

    case AppConstants.TEMPLATE_INFO_SET_TABLES_AUTO_RELEASE_ENABLED: {
      if (isEmpty(state.template)) {
        return state;
      }
      const { tableIds, enabled } = payload;
      return {
        ...state,
        templateTables: state.templateTables.map((templateTable) =>
          tableIds.includes(templateTable.tableId)
            ? { ...templateTable, autoReleaseEnabled: enabled }
            : templateTable
        ),
      };
    }

    case AppConstants.CLEAR_APPLY_TEMPLATE_WARNINGS: {
      return {
        ...state,
        warnings: undefined,
      };
    }

    case AppConstants.APPLY_CHECK_TEMPLATE_STARTING: {
      return {
        ...state,
        checkRequestInProgress: true,
        warnings: undefined,
      };
    }

    case AppConstants.APPLY_CHECK_TEMPLATE_FAILED: {
      return {
        ...state,
        checkRequestInProgress: false,
        warnings: undefined,
      };
    }

    case AppConstants.APPLY_CHECK_TEMPLATE_DONE: {
      return {
        ...state,
        checkRequestInProgress: false,
        warnings: toJSON(payload.warning),
      };
    }

    case AppConstants.GENERATE_TEMPLATE_STARTING:
    case AppConstants.APPLY_TEMPLATE_STARTING:
    case AppConstants.TEMPLATE_INFO_SAVE_ALL_STARTING:
      return {
        ...state,
        requestInProgress: true,
      };
    case AppConstants.TEMPLATE_INFO_FETCH_ALL_STARTING:
      return {
        ...initialState,
        requestInProgress: true,
      };

    case AppConstants.GENERATE_TEMPLATE_FAILED:
    case AppConstants.APPLY_TEMPLATE_FAILED:
    case AppConstants.TEMPLATE_INFO_SAVE_ALL_FAILED:
    case AppConstants.APPLY_TEMPLATE_DONE: {
      return {
        ...state,
        requestInProgress: false,
      };
    }

    case AppConstants.TEMPLATE_INFO_FETCH_ALL_FAILED:
      return {
        ...state,
        ...initialState,
        requestInProgress: false,
        failedToLoad: true,
      };
    case AppConstants.TEMPLATE_INFO_DRAG_SELECT_CURRENT_INVENTORY_SQUARE: {
      if (!state.dragSelectInProgress) {
        return state;
      }
      return {
        ...state,
        currentDragSelectionItem: payload,
      };
    }
    case AppConstants.TEMPLATE_INFO_DRAG_SELECT_START_INVENTORY_SQUARE: {
      return {
        ...state,
        dragSelectStartItem: payload,
      };
    }
    case AppConstants.TEMPLATE_INFO_DRAG_SELECT_TOGGLE_STATUS: {
      return {
        ...state,
        dragSelectInProgress: payload,
      };
    }
    case AppConstants.TEMPLATE_INFO_SELECT_DYNAMIC_OPTIMIZATION_TIME: {
      return {
        ...state,
        selectedDynamicOptimizationTimeBeforeServiceMinutes: payload,
      };
    }
    case AppConstants.TEMPLATE_INFO_CLEAR_DYNAMIC_OPTIMIZATION_TIME: {
      return {
        ...state,
        selectedDynamicOptimizationTimeBeforeServiceMinutes: null,
      };
    }
    default:
      return state;
  }
}

function updateStateWithNewTemplateInfo(
  templateInfoBase: SeatingTemplateInfoSpec,
  state: State
): State {
  const { nextId, template, templateInfoByTime, templateInfos, tickets } = state;
  if (!templateInfoBase.startTime) {
    return state;
  }
  const newTemplateInfos = templateInfos.concat([
    {
      ...templateInfoBase,
      id: nextId,
      price: getPrice(
        templateInfoByTime,
        tickets,
        templateInfoBase.ticketTypeId,
        templateInfoBase.startTime
      ),
      seatingTemplateId: template.id,
      tableState: 'AVAILABLE',
    },
  ]);
  return {
    ...state,
    nextId: nextId - 1,
    templateInfos: newTemplateInfos,
    templateInfoByTime: getTemplateInfoByTime(template, newTemplateInfos),
  };
}

/**
 * Given an action related to bookable ranges on a table element (i.e., adding or removing a range for a given ticket type), this
 * function computes the new ranges for the table element
 */
function manageBookableRangesForTable(
  ticketTypeConfigs: dashboard.ISeatingTemplateTableTicketTypeConfig[],
  ticketTypeId: number,
  serviceStartTime: string,
  serviceEndTime: string,
  mode: 'ADD' | 'REMOVE',
  rangeStartTime: LocalDateTime,
  rangeEndTime: LocalDateTime
) {
  // Get the config associated with the ticket type for which availability is being modified.
  // If it doesn't exist, create one.
  const ticketTypeTableConfigForTicketType: dashboard.ISeatingTemplateTableTicketTypeConfig =
    ticketTypeConfigs.find((config) => config.ticketTypeId === ticketTypeId) ??
    getNewTicketTypeConfigWithEmptyBookableRanges(ticketTypeId);

  // In a future world of diminishing constraints, get the relavant conditionalRange object. For now,
  // there will be at most one.
  const { templateRange, otherRanges: unchangedRanges } =
    getConditionalRangeTemplateForSTTTicketTypeConfig(ticketTypeTableConfigForTicketType);

  const bookableRangeSet = getUpdatedBookableRangeSet(
    templateRange,
    { mode, start: rangeStartTime, end: rangeEndTime },
    serviceStartTime,
    serviceEndTime
  );

  // Transform the RangeSet into the BookableRange model
  const bookableRangesFromRangeSet: dashboard.ISeatingTemplateTableExperienceBookableRange[] =
    getBookableRangesFromRangeSet(bookableRangeSet);

  const existingTicketTypeConfigsForOtherTicketTypes =
    ticketTypeConfigs.filter((config) => config.ticketTypeId !== ticketTypeId) ?? [];

  const isFullServiceApplicationDesired = getIsFullServiceApplicationDesired(
    bookableRangesFromRangeSet,
    serviceStartTime,
    serviceEndTime
  );

  return {
    isFullServiceApplicationDesired,
    bookableRangesFromRangeSet,
    existingTicketTypeConfigsForOtherTicketTypes,
    unchangedRanges,
    templateRange,
  };
}

function getBookableRangesFromRangeSet(
  rangeSet: RangeSet<LocalDateTime>
): dashboard.ISeatingTemplateTableExperienceBookableRange[] {
  return rangeSet.ranges().map(({ low, high }) => ({
    startTimeInclusive: low.toLocalTime().toString(),
    endTimeExclusive: high.toLocalTime().toString(),
  }));
}

function replaceTicketTypesForSeatingTemplateTables(
  state: State,
  tableIds: Set<number>,
  getNewTicketTypeIds: (existingTicketTypeIds: number[]) => number[]
): State {
  return {
    ...state,
    templateTables: map(state.templateTables, (templateTable) => {
      const newTicketTypeIds = getNewTicketTypeIds(templateTable.ticketTypeId);

      const existingTicketTypeConfigs = templateTable.ticketTypeConfig ?? [];

      const existingTicketTypeIdToConfig: Map<
        number,
        dashboard.ISeatingTemplateTableTicketTypeConfig
      > = new Map();

      existingTicketTypeConfigs.forEach((config) => {
        if (config.ticketTypeId) {
          existingTicketTypeIdToConfig.set(config.ticketTypeId, config);
        }
      });

      // Get or create a ticketTypeConfig for each of the new ticketTypeIds to apply
      const newTicketTypeConfigs = newTicketTypeIds.map(
        (ticketTypeId) =>
          existingTicketTypeIdToConfig.get(ticketTypeId) ??
          getNewTicketTypeConfigForFullServiceTicketType(ticketTypeId)
      );

      if (tableIds.has(templateTable.tableId)) {
        return {
          ...templateTable,
          ticketTypeId: newTicketTypeIds,
          ticketTypeConfig: newTicketTypeConfigs,
        };
      }
      return templateTable;
    }),
  };
}

export function getNewTicketTypeConfigForFullServiceTicketType(ticketTypeId: number) {
  return {
    ticketTypeId,
    conditionalRanges: [{ bookableRanges: undefined }],
  };
}

export function getNewTicketTypeConfigWithEmptyBookableRanges(ticketTypeId: number) {
  return {
    ticketTypeId,
    conditionalRanges: [{ bookableRanges: { bookableRange: [] } }],
  };
}

function getIsFullServiceApplicationDesired(
  bookableRangesFromRangeSet: dashboard.ISeatingTemplateTableExperienceBookableRange[] = [],
  seatingTemplateStartTimeInclusive: string,
  seatingTemplateEndTimeInclusive: string
) {
  if (bookableRangesFromRangeSet.length !== 1) {
    return false;
  }

  const desiredBookableRange = bookableRangesFromRangeSet[0];

  return (
    desiredBookableRange.startTimeInclusive === seatingTemplateStartTimeInclusive &&
    !!desiredBookableRange.endTimeExclusive &&
    fastParseTime(desiredBookableRange.endTimeExclusive).equals(
      fastParseTime(seatingTemplateEndTimeInclusive).plusMinutes(15)
    )
  );
}

/**
 * If a template table combination doesn't have its own ticket type configs and is inheriting from the
 * intersection of its underlying tables, this function will initialize its own ticket type configs to
 * a copy of those inherited configs.
 *
 * This provides a clean user experience when a user first edits ticket type configs on a table combo that is currently inheriting.
 */
function initializeTableCombinationConfigsFromConstituentTables(
  state: State,
  tableCombinationId: number
) {
  const tableIdsByTableCombinationId = new Map(
    (state.template.floorplan?.tableCombination ?? []).map((tableCombination) => [
      tableCombination.id,
      tableCombination.tableId,
    ])
  );
  const templateTablesByTableId = new Map(
    state.templateTables.map((table) => [table.tableId, table])
  );
  const constituentTableIds = tableIdsByTableCombinationId.get(tableCombinationId) ?? [];
  const constituentTemplateTables = constituentTableIds
    .map((tableId) => templateTablesByTableId.get(tableId))
    .filter(filterNilValues);

  const intersectedRangeSetsByTicketTypeId = getInheritedExperienceInfoOnTableCombos(
    constituentTemplateTables,
    constituentTableIds,
    state.template.startTime,
    state.template.endTime
  );

  return Array.from(intersectedRangeSetsByTicketTypeId.entries()).map(
    ([ticketTypeId, bookableRangeSet]) => {
      return {
        ticketTypeId: Number(ticketTypeId),
        conditionalRanges: [
          {
            bookableRanges: {
              bookableRange: getBookableRangesFromRangeSet(bookableRangeSet),
            },
          },
        ],
      };
    }
  );
}

/**
 * Gets the conditional range to use as a "template" for bookable ranges related to a specific experience on a specific table.
 * This represents the bookable ranges that, for example, are displayed in the advanced grid.
 *
 * Example: If experience A is applied on a table from 11:30-3pm from -Infinity to 4 days prior to the night of service, and then
 * is not offered at all (empty bookable ranges) within 4 days of service, the 11:30-3pm range is the "template".
 *
 * If and when we allow bookable ranges to vary across conditions, or when we allow multiple optimization tiers / windows,
 * we'll have to consider how that interacts with this logic. At the time of writing, though, we'll only ever have 2
 * optimization tiers / windows.
 *
 * "Add" optimization
 * {
 *   conditionalTableRange: [
 *     {
 *       bookableRanges: [{ startTimeInclusive: [16, 30], endTimeExclusive: [20, 0] }], <TEMPLATE RANGE>
 *       condition: {
 *         effectiveAtMinutesBefore: 1440,
 *       },
 *     },
 *     {
 *       bookableRanges: [],
 *     },
 *   ],
 * };
 *
 * "Remove" optimization
 * {
 *   conditionalTableRange: [
 *     {
 *       bookableRanges: [],
 *       condition: {
 *         effectiveAtMinutesBefore: 1440,
 *       },
 *     },
 *     {
 *      bookableRanges: null, <TEMPLATE RANGE / full-service application>
 *     }
 *   ],
 * };
 *
 */
export function getConditionalRangeTemplateForSTTTicketTypeConfig(
  configForSelectedTicketType: dashboard.ISeatingTemplateTableTicketTypeConfig
): {
  templateRange: dashboard.ISeatingTemplateConditionalRange;
  otherRanges: dashboard.ISeatingTemplateConditionalRange[];
} {
  // If only one range exists, that must be the template
  if (configForSelectedTicketType.conditionalRanges?.length === 1) {
    return { templateRange: configForSelectedTicketType.conditionalRanges?.[0], otherRanges: [] };
  }

  // Find a restricted applied experience time range for the table.
  // Specifically finds the item that does not have an empty array bookableRange value [],
  // which indicates that the experience is not offered at all on the table
  const appliedTimeRangeConditional = configForSelectedTicketType.conditionalRanges?.find(
    (conditionalRange) =>
      !(
        Array.isArray(conditionalRange.bookableRanges?.bookableRange) &&
        isEmpty(conditionalRange.bookableRanges.bookableRange)
      )
  );

  // Filter out the applied range to get all other ranges
  const otherRanges =
    configForSelectedTicketType.conditionalRanges?.filter(
      (conditionalRange) => conditionalRange !== appliedTimeRangeConditional
    ) || [];

  return {
    templateRange: appliedTimeRangeConditional || {},
    otherRanges,
  };
}

/**
 * Selects the relevant conditional range that will be effective at a given time before start of service
 */
export function getConditionalRangeAtTimeBeforeServiceForTicketType(
  configsForSelectedTicketType: dashboard.ISeatingTemplateTableTicketTypeConfig,
  timeBeforeServiceMinutes: number | null
): dashboard.ISeatingTemplateConditionalRange {
  const sortedConfigs = [...(configsForSelectedTicketType?.conditionalRanges ?? [])].sort(
    (a, b) => {
      const aTimeBeforeServiceMonutes =
        a.condition?.effectiveAtMinutesBefore ?? Number.MAX_SAFE_INTEGER;
      const bTimeBeforeServiceMonutes =
        b.condition?.effectiveAtMinutesBefore ?? Number.MAX_SAFE_INTEGER;
      return aTimeBeforeServiceMonutes - bTimeBeforeServiceMonutes;
    }
  );

  // Find the first entry where effectiveAtMinutesBefore is >= timeBeforeServiceMinutes
  const rangeAtTime =
    sortedConfigs.find(
      (range) =>
        range.condition?.effectiveAtMinutesBefore != null &&
        range.condition.effectiveAtMinutesBefore >=
          (timeBeforeServiceMinutes ?? Number.MAX_SAFE_INTEGER)
    ) ?? sortedConfigs.find((range) => range.condition?.effectiveAtMinutesBefore == null); // Picks the default case if no match

  return rangeAtTime || {};
}

function createNewConditionalRangesWithOptimization(
  mode: 'ADD' | 'REMOVE',
  bookableRangesTemplate: dashboard.ISeatingTemplateConditionalRange,
  effectiveAtMinutesBefore: number | undefined
) {
  if (!effectiveAtMinutesBefore) {
    return [{ ...bookableRangesTemplate, condition: undefined }];
  }

  if (mode === 'ADD') {
    return [
      { bookableRanges: { bookableRange: [] } },
      { ...bookableRangesTemplate, condition: { effectiveAtMinutesBefore } },
    ];
  } else {
    return [
      { ...bookableRangesTemplate, condition: undefined },
      { bookableRanges: { bookableRange: [] }, condition: { effectiveAtMinutesBefore } },
    ];
  }
}

function getUpdatedSTTTicketTypeConfig(
  ticketTypeId: number,
  isFullServiceApplicationDesired: boolean,
  bookableRangesFromRangeSet: dashboard.ISeatingTemplateTableExperienceBookableRange[],
  templateRange: dashboard.ISeatingTemplateConditionalRange,
  unchangedRanges: dashboard.ISeatingTemplateConditionalRange[]
): dashboard.ISeatingTemplateTableTicketTypeConfig {
  const newConditionalRange = isFullServiceApplicationDesired
    ? { ...templateRange, bookableRanges: undefined }
    : { ...templateRange, bookableRanges: { bookableRange: bookableRangesFromRangeSet } };
  return {
    ticketTypeId,
    conditionalRanges: [...unchangedRanges, newConditionalRange],
  };
}

/**
 * Given an additional conditionalRange and an action descriptor (mode, start, end), compute the RangeSet representing
 * the initial state from the conditionalRange with the given action applied
 */
function getUpdatedBookableRangeSet(
  conditionalRanges: dashboard.SeatingTemplateConditionalRange | undefined,
  { mode, start, end }: { mode: 'ADD' | 'REMOVE'; start: LocalDateTime; end: LocalDateTime },
  seatingTemplateStartTime: string,
  seatingTemplateEndTime: string
) {
  // Get existing set of bookable ranges
  const existingBookableRangeSet = rangeSetFromRestrictions(
    conditionalRanges?.bookableRanges?.bookableRange ?? [],
    ({ startTimeInclusive }) => startTimeInclusive,
    ({ endTimeExclusive }) => endTimeExclusive
  );

  const isFullServiceApplication = conditionalRanges?.bookableRanges === undefined;

  switch (mode) {
    case 'ADD':
      if (isFullServiceApplication) {
        return getSeatingTemplateRangeSet(seatingTemplateStartTime, seatingTemplateEndTime);
      }
      return existingBookableRangeSet.addRange(range(start, end));
    case 'REMOVE':
    default:
      // If we're "removing" a ticket type at a time on a table that is currently a full-service application
      // (no existing bookable ranges), we first need to create a range that covers the duration of the service,
      // and then subtract the range that we wish to remove
      if (isFullServiceApplication) {
        return getSeatingTemplateRangeSet(
          seatingTemplateStartTime,
          seatingTemplateEndTime
        ).removeRange(range(start, end));
      }
      return existingBookableRangeSet.removeRange(range(start, end));
  }
}

/**
 * Get a range set representing the bookable range of the entire seating template (service)
 */
export function getSeatingTemplateRangeSet(
  seatingTemplateStartTime: string,
  seatingTemplateEndTime: string
) {
  const serviceRangeSet = RangeSet.empty<LocalDateTime>();
  const { start: serviveStart, end: serviceEnd } = adjustRangeForBusinessDay(
    TEMPLATE_INFO_BUSINESS_DAY,
    seatingTemplateStartTime,
    seatingTemplateEndTime
  );
  return serviceRangeSet.addRange(range(serviveStart, serviceEnd.plusMinutes(15)));
}
